import { ICellRendererAngularComp } from '@ag-grid-community/angular';
import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay';
import { NgClass, NgStyle } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { set } from 'lodash-es';

import { IconComponent } from '@shared/components/icon/icon.component';
import { InputComponent } from '@shared/components/input/input.component';
import { ClickOutsideDirective } from '@shared/directives/click-outside.directive';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { ActivitySubType, ActivityType } from '@shared/services/gql.service';

import {
  BeInlineCategoryDropdownOption,
  BeInlineCategoryDropdownParams,
} from './be-inline-category-dropdown.model';
import {
  BeActivitiesAttributesModalRowData,
  BeActivityTypes,
} from '../be-activities-attributes-modal/be-activities-attributes-modal.model';
import { BeInlineCategoryOptionComponent } from './be-inline-category-item/be-inline-category-option.component';

@Component({
  imports: [
    IconComponent,
    CdkConnectedOverlay,
    CdkOverlayOrigin,
    TooltipDirective,
    NgStyle,
    InputComponent,
    FormsModule,
    NgClass,
    BeInlineCategoryOptionComponent,
    ClickOutsideDirective,
  ],
  styleUrls: ['be-inline-category-dropdown.component.scss'],
  templateUrl: 'be-inline-category-dropdown.component.html',
})
export class BeInlineCategoryDropdownComponent implements ICellRendererAngularComp {
  private changeDetectorRef = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  activityType: '' | BeActivityTypes = '';
  categories: BeInlineCategoryDropdownOption[] = [];
  categoryTooltipPositions: ConnectedPosition[] = [
    {
      offsetX: -11,
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
    },
  ];
  disableChangingCategoriesTooltip = 'Cannot change cost category for existing activities';
  disabled = false;
  dropdownOpened = false;
  filteredCategories: BeInlineCategoryDropdownOption[] = [];
  filterText = '';
  isGenerated = true;
  isOpenedByDefault = false;
  msg = 'Auxilius Product restricts Category and Activity Name for Investigator and Discount.';
  params!: BeInlineCategoryDropdownParams;
  positions: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
    },
    {
      offsetY: -34,
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
    },
  ];
  selectedCategory: BeInlineCategoryDropdownOption | null | undefined = null;
  selectedCategoryName = '';
  topLevelTooltipPositions: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
    },
  ];

  agInit(params: BeInlineCategoryDropdownParams) {
    this.params = params;

    params.categories$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      this.categories = value;
      this.onFilter();
      this.updateSelectedName();
    });

    if (this.params.node.data?.category) {
      this.selectedCategory = this.categories.find(
        (c) =>
          c.type === this.params.node.data?.activity_type &&
          c.id === this.params.node.data?.category
      );
      this.selectedCategoryName = this.selectedCategory?.name || '';

      this.activityType = this.params.node.data?.activity_type;
      this.isGenerated = this.params.node.data?.isGenerated;

      switch (this.params.node.data?.activity_type) {
        case ActivitySubType.ACTIVITY_INVESTIGATOR_PATIENT_INVOICEABLES:
        case ActivitySubType.ACTIVITY_INVESTIGATOR_PATIENT_VISITS:
        case ActivitySubType.ACTIVITY_INVESTIGATOR_SITE_INVOICEABLES:
          this.disabled = true;
          this.selectedCategoryName = `Investigator > ${this.selectedCategoryName}`;
          break;
        case ActivityType.ACTIVITY_DISCOUNT:
          this.disabled = true;
          break;
        default:
          this.disabled = false;
          break;
      }
    }

    this.onFilter();

    if (params.node.data?.shouldOpenByDefault) {
      this.isOpenedByDefault = true;
      this.onDropdown();
      this.changeDetectorRef.detectChanges();
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      set(params.data, 'shouldOpenByDefault', false);
    }
  }

  isSelected(option: BeInlineCategoryDropdownOption): boolean {
    return (
      !!this.selectedCategory &&
      this.selectedCategory.id === option.id &&
      this.selectedCategory?.name === option.name
    );
  }

  onDropdown() {
    if (!this.disabled) {
      this.onFilter();
      this.dropdownOpened = true;
    }
  }

  onDropdownAdd(newCategoryName: string, option: BeInlineCategoryDropdownOption): void {
    if (!option.disabled) {
      this.onDropdownSelected(this.params.onCategoryAdd(newCategoryName, option));
    } else {
      this.params.onCategoryAdd(newCategoryName, option);
    }
  }

  onDropdownEdit(newCategoryName: string, option: BeInlineCategoryDropdownOption): void {
    this.params.onCategoryRename(newCategoryName, option);
  }

  onDropdownSelected(option: BeInlineCategoryDropdownOption) {
    const { id, type } = option;

    this.dropdownOpened = false;
    // clean state
    this.params.node.updateData(<BeActivitiesAttributesModalRowData>{
      ...this.params.node.data,
      activity_type: '',
      category: '',
    });

    // refill
    this.params.node.updateData(<BeActivitiesAttributesModalRowData>{
      ...this.params.node.data,
      activity_type: type,
      category: id,
    });
    this.params.onCategoryChange(this.params, id, type);
  }

  onFilter() {
    const usedCategories = this.params.usedCategories();
    const categories = this.categories.map((c) => {
      const bool = this.isGenerated ? false : c.type !== this.activityType;

      return {
        ...c,
        disabled: c.type === ActivityType.ACTIVITY_INVESTIGATOR || usedCategories[c.type] || bool,
        disabledTooltip: bool ? this.disableChangingCategoriesTooltip : '',
      };
    });

    if (this.filterText) {
      this.filteredCategories = categories.filter((c) => {
        return c.name.toLowerCase().includes(this.filterText.toLowerCase().trim());
      });
    } else {
      this.filteredCategories = categories;
    }
  }

  refresh() {
    return false;
  }

  trackByFn(_: number, c: BeInlineCategoryDropdownOption) {
    return c.id;
  }

  private updateSelectedName() {
    this.selectedCategory = this.categories.find(
      (c) =>
        c.type === this.params.node.data?.activity_type && c.id === this.params.node.data?.category
    );
    this.selectedCategoryName = this.selectedCategory?.name || '';
  }
}
